/**
 * Freeze the page covered by the game theater overlay without unmounting it.
 *
 * Ownership:
 * - This module owns freeze count, the html marker, and the size snapshot.
 * - CSS (`html[data-theater-overlay="open"] [data-theater-overlay-page]`) owns
 *   skip-paint / no-hover / CSS animation pause. It must NOT key off React
 *   class names, so unfreeze is synchronous with overlay unmount (before
 *   scroll unlock) and scroll position survives every viewport.
 * - `useTheaterOverlayFrozen()` is for JS work CSS cannot stop (canvas, rAF,
 *   setInterval, Rive). New animation loops behind the listing should use it.
 *
 * Overlay close is still `router.back()` at the same scroll position.
 */

export const THEATER_OVERLAY_ATTR = "data-theater-overlay";
export const THEATER_OVERLAY_PAGE_ATTR = "data-theater-overlay-page";

let freezeCount = 0;
const listeners = new Set<() => void>();

function getTheaterOverlayPage(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.querySelector(`[${THEATER_OVERLAY_PAGE_ATTR}]`);
}

/** Snapshot laid-out size before skip-paint so the page cannot collapse to 0. */
function snapshotTheaterOverlayPageSize(page: HTMLElement) {
  const width = Math.ceil(page.offsetWidth);
  const height = Math.ceil(page.offsetHeight);
  if (width > 0 && height > 0) {
    page.style.containIntrinsicSize = `${width}px ${height}px`;
  }
}

function applyTheaterOverlayFreezeDom() {
  const page = getTheaterOverlayPage();
  if (page) snapshotTheaterOverlayPageSize(page);
  document.documentElement.setAttribute(THEATER_OVERLAY_ATTR, "open");
}

function clearTheaterOverlayFreezeDom() {
  // Unhide first so size containment ends at the current viewport, then drop
  // the snapshot. Reverse order can clamp scrollY on rotate/split-screen close.
  document.documentElement.removeAttribute(THEATER_OVERLAY_ATTR);
  const page = getTheaterOverlayPage();
  if (page) page.style.containIntrinsicSize = "";
}

function emitTheaterOverlayFreeze() {
  for (const listener of listeners) {
    listener();
  }
}

export function isTheaterOverlayFrozen(): boolean {
  return freezeCount > 0;
}

/** Tab hidden or theater overlay covering this page — skip background work. */
export function isBackgroundWorkPaused(): boolean {
  return (typeof document !== "undefined" && document.hidden) || freezeCount > 0;
}

export function subscribeTheaterOverlayFreeze(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function acquireTheaterOverlayFreeze(): () => void {
  freezeCount++;
  if (freezeCount === 1 && typeof document !== "undefined") {
    applyTheaterOverlayFreezeDom();
    emitTheaterOverlayFreeze();
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    freezeCount = Math.max(0, freezeCount - 1);
    if (freezeCount === 0 && typeof document !== "undefined") {
      clearTheaterOverlayFreezeDom();
      emitTheaterOverlayFreeze();
    }
  };
}

/** Jest helper so freeze count cannot leak across suites. */
export function resetTheaterOverlayFreezeForTests() {
  freezeCount = 0;
  if (typeof document !== "undefined") {
    clearTheaterOverlayFreezeDom();
  }
  emitTheaterOverlayFreeze();
}
